import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOnboardingActions } from "@/hooks/queries/use-onboarding";
import { cn } from "@/lib/utils";

interface Pricing {
  freePeopleLimit: number;
  teamPricePerSeatUsd: number;
  trialDays: number;
}

function PlanOption({
  selected,
  onSelect,
  title,
  price,
  lines,
  badge,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  price: string;
  lines: string[];
  badge?: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        "w-full rounded-xl border p-4 text-left transition-colors",
        selected ? "border-primary bg-primary/[0.06]" : "border-border hover:border-primary/50"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
              selected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground"
            )}
            aria-hidden
          >
            {selected && <Check className="h-3 w-3" />}
          </span>
          <span className="font-medium">{title}</span>
          {badge && (
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary">{badge}</span>
          )}
        </div>
        <span className="text-sm font-medium">{price}</span>
      </div>
      <ul className="mt-2 space-y-0.5 pl-6 text-sm text-muted-foreground">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </button>
  );
}

const AI_CHOICES = [
  { name: "Claude", detail: "each person's Claude subscription or Anthropic API key", available: true },
  { name: "OpenAI Codex", detail: "coming soon", available: false },
  { name: "Gemini", detail: "coming soon", available: false },
];

/**
 * The plan step of onboarding: a new organization's admin picks Free or Team
 * once (Team starts a no-card trial). Everyone they add uses the same plan;
 * members never see this screen.
 */
export function PlanStep({
  organizationName,
  people,
  pricing,
}: {
  organizationName: string;
  people: number;
  pricing: Pricing;
}) {
  const { selectPlan, joinManagedAiWaitlist } = useOnboardingActions();
  const freeFits = people <= pricing.freePeopleLimit;
  const [plan, setPlan] = useState<"free" | "team">("team");
  const error = selectPlan.error instanceof Error ? selectPlan.error.message : null;

  return (
    <div className="w-full space-y-8">
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Choose a plan for {organizationName}
        </h1>
        <p className="text-sm text-muted-foreground">
          Everyone you add uses this plan. You can change it later under Billing.
        </p>
      </div>

      <div role="radiogroup" aria-label="Plan" className="space-y-3">
        <PlanOption
          selected={plan === "team"}
          onSelect={() => {
            setPlan("team");
          }}
          title="Team"
          badge={`${String(pricing.trialDays)} days free`}
          price={`$${String(pricing.teamPricePerSeatUsd)} / person / month`}
          lines={[
            "Any number of people; you pay only for people on a team",
            `No card needed to start; add one within ${String(pricing.trialDays)} days`,
          ]}
        />
        {freeFits && (
          <PlanOption
            selected={plan === "free"}
            onSelect={() => {
              setPlan("free");
            }}
            title="Free"
            price="$0"
            lines={[`Up to ${String(pricing.freePeopleLimit)} people`, "Everything else included"]}
          />
        )}
        <div className="rounded-xl border border-dashed border-border p-4 text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="font-medium">
              Managed AI <span className="font-normal text-muted-foreground">· coming soon</span>
            </span>
            {joinManagedAiWaitlist.isSuccess ? (
              <span className="text-muted-foreground">You&apos;re on the waitlist ✓</span>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={joinManagedAiWaitlist.isPending}
                onClick={() => {
                  joinManagedAiWaitlist.mutate();
                }}
              >
                Join the waitlist
              </Button>
            )}
          </div>
          <p className="mt-1 text-muted-foreground">
            We provide the AI, so nobody on your team needs their own account.
          </p>
        </div>
        <p className="text-center text-sm text-muted-foreground">
          Need single sign-on, private models or a contract?{" "}
          <a className="underline underline-offset-4" href="mailto:support@mycompbox.com?subject=CompBox%20Enterprise">
            Contact us about Enterprise
          </a>
        </p>
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-medium">Which AI will your team use?</h2>
        <ul className="space-y-2 text-sm">
          {AI_CHOICES.map((ai) => (
            <li key={ai.name} className={cn("flex items-center gap-2", !ai.available && "text-muted-foreground")}>
              <span
                className={cn(
                  "flex h-4 w-4 items-center justify-center rounded border",
                  ai.available ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground"
                )}
                aria-hidden
              >
                {ai.available && <Check className="h-3 w-3" />}
              </span>
              <span>
                <span className="font-medium">{ai.name}</span> — {ai.detail}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">
          The AI runs on each person&apos;s own computer, with their own AI account. CompBox never sees
          your AI keys or logins. Work sent to a computer runs without stopping to ask for approval at
          each step, so connect only computers you trust.
        </p>
      </div>

      {error && (
        <p className="text-sm text-red-400" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-center">
        <Button
          className="min-w-40"
          disabled={selectPlan.isPending}
          onClick={() => {
            selectPlan.mutate(plan);
          }}
        >
          {selectPlan.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : plan === "team" ? (
            "Start free trial"
          ) : (
            "Start on Free"
          )}
        </Button>
      </div>
    </div>
  );
}
